import { isNil, upperFirst } from 'lodash'
import { flow, types } from 'mobx-state-tree'
import { v4 as uuidV4 } from 'uuid'
import { RequestHandler } from '../../api/RequestHandler'
import { ILadderModel } from '../../../types/models'
import { LadderMetricIds } from '../../reference'
import { convertOversToBallsTotal } from '@ias-shared/cricket-logic'

const LadderCompetitionLadderTeamDatumModel = types
  .model('LadderCompetitionLadderTeamDatumModel', {
    value: types.number,
    displayValue: types.string,
    ladderMetricId: types.maybeNull(types.number),
  })
  .preProcessSnapshot(sn => {
    if (!sn) return sn
    if (isNil(sn.value)) sn.value = 0
    if (isNil(sn.displayValue)) sn.displayValue = '0'
    return sn
  })

export const LadderCompetitionLadderTeamModel = types
  .model('LadderCompetitionLadderTeamModel', {
    competitionTeamId: types.maybeNull(types.string),
    teamId: types.maybeNull(types.string),
    rank: types.number,
    teamName: types.maybeNull(types.string),
    ladderTeamData: types.array(LadderCompetitionLadderTeamDatumModel),
    // NRR calc specific properties below
    hasModified: false,
    hasModifiedRank: '',
  })
  .preProcessSnapshot(sn => {
    if (!sn) return sn
    if (isNil(sn.rank)) sn.rank = 1
    return sn
  })

export const LadderCompetitionLadderModel = types
  .model('LadderCompetitionLadderModel', {
    id: types.string,
    name: types.maybeNull(types.string),
    order: types.number,
    competitionLadderId: types.maybeNull(types.string),
    ladderTeams: types.array(LadderCompetitionLadderTeamModel),
  })
  .preProcessSnapshot(sn => {
    if (!sn) return sn
    if (isNil(sn.id)) sn.id = uuidV4()
    if (isNil(sn.order)) sn.order = 1
    return sn
  })

export const LadderCompetitionLaddersColumnsModel = types.model('LadderCompetitionLaddersColumnsModel', {
  ladderMetricId: types.number,
  title: types.string,
  order: types.number,
})

export const LadderCompetitionLaddersModel = types.model('LadderCompetitionLaddersModel', {
  id: types.string,
  ladderColumns: types.array(LadderCompetitionLaddersColumnsModel),
  ladders: types.maybeNull(types.array(LadderCompetitionLadderModel)),
})

export const LadderCompetitionPoolsModel = types.model('LadderCompetitionPoolsModel', {
  id: types.string,
  name: types.string,
  order: types.number,
  ladders: types.array(LadderCompetitionLadderModel),
})

export const LadderCompetitionStageModel = types.model('LadderCompetitionStageModel', {
  id: types.string,
  name: types.string,
  order: types.number,
  hasLadder: types.boolean,
  competitionPools: types.maybeNull(types.array(LadderCompetitionPoolsModel)),
  competitionLadders: types.array(LadderCompetitionLaddersModel),
})

export const LadderModel = types.model('LadderModel', {
  competitionId: types.string,
  name: types.string,
  competitionStages: types.array(LadderCompetitionStageModel),
})

const LaddersMap = types.model('LaddersMap', {
  id: types.identifier,
  ladder: LadderModel,
})

type LadderColumnsMapping = { clsp: string; sr: string | [string, string]; display: boolean }
const LADDER_COLUMNS: LadderColumnsMapping[] = [
  { clsp: 'M', sr: 'played', display: true },
  { clsp: 'W', sr: 'win', display: true },
  { clsp: 'L', sr: 'loss', display: true },
  { clsp: 'T', sr: 'draw', display: true },
  { clsp: 'N/R', sr: 'no_result', display: true },
  { clsp: 'PT', sr: 'points', display: true },
  { clsp: 'NRR', sr: 'net_run_rate', display: true },
  { clsp: 'RFOR', sr: 'runs_for', display: false },
  { clsp: 'RAGST', sr: 'runs_against', display: false },
  { clsp: 'OVFOR', sr: 'overs_for', display: false },
  { clsp: 'OVAGST', sr: 'overs_against', display: false },
  { clsp: 'FOR', sr: ['runs_for', 'overs_for'], display: true },
  { clsp: 'AGST', sr: ['runs_against', 'overs_against'], display: true },
]

const LaddersStore = types
  .model('LaddersStore', {
    results: types.map(LaddersMap),
    state: types.enumeration('State', ['pending', 'done', 'error']),
  })
  .views(() => {
    const translateInfoToStandings = (data: any) => {
      return [
        {
          type: 'total',
          groups: [
            {
              id: data.tournament.id,
              name: data.tournament.name,
              live: false,
              team_standings: data.groups
                .map((g: any) => {
                  return g.teams
                    .map((t: any) => {
                      const dataCols = Object.assign(
                        {},
                        ...LADDER_COLUMNS.map((c: LadderColumnsMapping) => {
                          if (Array.isArray(c.sr)) {
                            return {
                              [c.sr[0]]: 0,
                              [c.sr[1]]: 0,
                            }
                          } else {
                            return {
                              [c.sr]: 0,
                            }
                          }
                        })
                      )
                      return {
                        rank: 0,
                        team: t,
                        ...dataCols,
                      }
                    })
                    .sort((a: any, b: any) => {
                      return a.team.name > b.team.name ? 1 : -1
                    })
                })
                .flat(),
            },
          ],
          round: 1,
        },
      ]
    }

    const translate = (data: any, emptyStandings?: any): ILadderModel => {
      const standings = emptyStandings ?? data.standings ?? []
      return {
        competitionId: data.tournament.id,
        name: data.tournament.name,
        competitionStages:
          standings.map((f: any) => {
            return {
              id: `${data.tournament.id}_${f.round}`,
              name: upperFirst(f.type),
              order: f.round,
              hasLadder: true,
              competitionLadders: [
                {
                  id: `${data.tournament.id}_${f.round}_compLadders`,
                  ladderColumns: LADDER_COLUMNS.filter((c: LadderColumnsMapping) => c.display).map(
                    (c: LadderColumnsMapping, idx: number) => {
                      const colDef = LadderMetricIds.find(m => m.abbrev === c.clsp)
                      if (!colDef) return
                      return {
                        ladderMetricId: colDef.id,
                        title: colDef.abbrev,
                        order: idx + 1,
                      }
                    }
                  ),
                  ladders: f.groups.map((l: any) => {
                    return {
                      id: l.id,
                      competitionLadderId: `${data.tournament.id}_${f.round}_compLadders`,
                      name: l.name,
                      order: f.round,
                      ladderTeams: l.team_standings.map((t: any) => {
                        return {
                          competitionTeamId: t.team.id,
                          teamId: t.team.id,
                          rank: t.rank,
                          teamName: t.team.name,
                          ladderTeamData: LADDER_COLUMNS.map((c: LadderColumnsMapping) => {
                            const colDef = LadderMetricIds.find(m => m.abbrev === c.clsp)
                            if (!colDef) return
                            const adjustment =
                              colDef.name === 'MATCHES_PLAYED' && process.env.REACT_APP_MANUAL_NO_RESULT === 'true'
                                ? t['no_result'] ?? 0
                                : 0
                            return {
                              value:
                                !Array.isArray(c.sr) && (colDef.name === 'FOR_OVERS' || colDef.name === 'AGAINST_OVERS')
                                  ? convertOversToBallsTotal({ overs: t[c.sr] ?? 0 }) // NB: FOR_OVERS / AGAINST_OVERS 'value' is a balls total; 'displayValue' is the float-string representation
                                  : Array.isArray(c.sr)
                                  ? 0
                                  : (t[c.sr] ?? 0) + adjustment,
                              displayValue: Array.isArray(c.sr)
                                ? `${t[c.sr[0]] ?? '0'}/${t[c.sr[1]] ?? '0'}`
                                : `${(t[c.sr] ?? 0) + adjustment}`,
                              ladderMetricId: colDef.id,
                            }
                          }),
                        }
                      }),
                    }
                  }),
                },
              ],
            }
          }) ?? [],
      }
    }
    return {
      translateInfoToStandings,
      translate,
    }
  })
  .actions(self => {
    const getLadder = (competitionId?: string) => {
      if (!competitionId) return
      const data = self.results.get(competitionId)
      if (data) {
        return data
      }
      if (self.state === 'pending') {
        return null
      }
      fetchLadder(competitionId)
      return
    }

    const fetchLadder = flow(function* fetchLadder(competitionId: string) {
      self.state = 'pending'
      const method = 'GET'
      const url = `${process.env.REACT_APP_API_URL}${encodeURIComponent(
        `tournaments/sr:tournament:${competitionId}/standings`
      )}`
      try {
        const response = yield RequestHandler({
          method,
          url,
          headers: { 'sr-client-key': '123' },
        })
        if (response && response.ok) {
          const data = yield response.json()
          if (data) {
            let emptyStandings
            if (!data.standings) {
              // if there is no standings data, create baseline data for each team in this comp/tournament
              const ladderInfo = yield fetchLadderInfo(competitionId)
              if (ladderInfo) emptyStandings = self.translateInfoToStandings(ladderInfo)
            }
            self.results.put(
              LaddersMap.create({
                id: competitionId,
                ladder: self.translate(data, emptyStandings),
              })
            )
          }
          self.state = 'done'
        } else {
          self.state = 'done'
        }
      } catch (error) {
        console.warn('Error getting Ladder', error) // eslint-disable-line no-console
        self.state = 'error'
      }
    })

    const fetchLadderInfo = flow(function* fetchLadderInfo(competitionId: string) {
      self.state = 'pending'
      const method = 'GET'
      const url = `${process.env.REACT_APP_API_URL}${encodeURIComponent(
        `tournaments/sr:tournament:${competitionId}/info`
      )}`
      try {
        const response = yield RequestHandler({
          method,
          url,
          headers: { 'sr-client-key': '123' },
        })
        if (response && response.ok) {
          const data = yield response.json()
          if (data) {
            self.state = 'done'
            return data
          }
          self.state = 'done'
        } else {
          self.state = 'done'
        }
      } catch (error) {
        console.warn('Error getting LadderInfo', error) // eslint-disable-line no-console
        self.state = 'error'
      }
    })

    return {
      getLadder,
      fetchLadder,
      fetchLadderInfo,
    }
  })

export default LaddersStore
